import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';

// The firehose is a single setting, not a list: one Discord channel that newly
// opened issues from the listed repos are posted to.
class IssueFirehoseView extends BaseComponent {
    static properties = {
        _firehose: { type: Object, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _notice: { type: String, state: true },
        _formData: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
    };

    static styles = css`
        :host { display: block; }
    `;

    constructor() {
        super();
        this._firehose = null;
        this._loading = true;
        this._error = null;
        this._notice = null;
        this._formData = { discord_channel_id: '', repos: '' };
        this._saving = false;
    }

    async connectedCallback() {
        super.connectedCallback();
        await this._load();
    }

    async _load() {
        this._loading = true;
        this._error = null;
        try {
            this._setFirehose(await api.get('/api/issue-firehose'));
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    _setFirehose(firehose) {
        this._firehose = firehose;
        this._formData = {
            discord_channel_id: firehose?.DiscordChannelID ?? '',
            repos: (firehose?.Repos ?? []).join('\n'),
        };
    }

    async _save(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        this._notice = null;
        try {
            const saved = await api.put('/api/issue-firehose', {
                discord_channel_id: this._formData.discord_channel_id.trim(),
                repos: this._formData.repos.split(/[\s,]+/).filter(Boolean),
            });
            this._setFirehose(saved);
            this._notice = 'Saved.';
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _test() {
        this._error = null;
        this._notice = null;
        try {
            await api.post('/api/issue-firehose/test');
            this._notice = 'Test notification sent.';
        } catch (err) {
            this._error = err.message;
        }
    }

    _updateField(field, value) {
        this._formData = { ...this._formData, [field]: value };
    }

    render() {
        if (this._loading) {
            return html`<div class="text-center py-4"><span class="spinner-border"></span></div>`;
        }

        return html`
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Issue Firehose</h5>
                ${
                    this._firehose
                        ? html`
                    <button class="btn btn-outline-info btn-sm" title="Send test notification" @click="${this._test}">
                        <i class="bi bi-send"></i> Test
                    </button>
                `
                        : ''
                }
            </div>

            <p class="text-muted" style="font-size: 0.875rem;">
                Every newly opened issue in the repos below is posted to this channel. The events
                come from the D3-PO GitHub App, so each repo must have the app installed.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._notice ? html`<div class="alert alert-success">${this._notice}</div>` : ''}

            <form @submit="${(e) => this._save(e)}" style="max-width: 500px;">
                <div class="mb-3">
                    <label class="form-label">Discord Channel ID</label>
                    <input type="text" class="form-control" placeholder="e.g. 1234567890"
                           .value="${this._formData.discord_channel_id}"
                           @input="${(e) => this._updateField('discord_channel_id', e.target.value)}"
                           required>
                    <div class="form-text">Enable Developer Mode in Discord settings, then right-click the channel and Copy Channel ID</div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Repos</label>
                    <textarea class="form-control" rows="4" placeholder="d3-vtt&#10;d3-rasa-engine"
                              .value="${this._formData.repos}"
                              @input="${(e) => this._updateField('repos', e.target.value)}"></textarea>
                    <div class="form-text">Short repo names (not full org/repo), one per line</div>
                </div>

                <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                    ${this._saving ? html`<span class="spinner-border spinner-border-sm"></span>` : 'Save'}
                </button>
            </form>
        `;
    }
}

customElements.define('issue-firehose-view', IssueFirehoseView);
